import React, { useState } from "react";

// region Imports - External libraries
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Tabs, Tab, Box, Typography
} from "@material-ui/core";
// endregion Imports - External libraries
// region Imports - Languages
import useTranslation from "src/translations/useTranslation";
import { GlobalMessages, VehiclesModuleMessages } from "@shared/languages/interfaces";
// endregion Imports - Languages
// region Imports - Shared
import { Vehicle } from "@shared/interfaces/vehicle.interface";
// endregion Imports - Shared
// region Imports - Sub components
import WaterFactorControlTab from "./WaterFactorControlTab";
import WaterFactorHistoryTab from "./WaterFactorHistoryTab";
// endregion - Sub components
// region Imports - Styles
import { Container } from "./styles";
// endregion Imports - Styles

interface ModalWaterFactorControlProps {
  open: boolean;
  onClose: () => void;
  vehicle: Vehicle;
}
interface TabPanelProps {
  children?: React.ReactNode;
  index: any;
  value: any;
}

/**
 * A component that represents a single tab panel, displaying its content only when the associated tab is active.
 *
 * @param {TabPanelProps} props - The properties for the TabPanel component.
 * @param {ReactNode} props.children - The content to be displayed inside the tab panel.
 * @param {number} props.value - The currently selected tab index.
 * @param {number} props.index - The index of this tab panel.
 * @param {object} [props.other] - Additional properties to pass to the tab panel container.
 *
 * @return {JSX.Element} The rendered tab panel component.
 */
function TabPanel(props: TabPanelProps): JSX.Element {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box p={3}>
          <Typography component="div">{children}</Typography>
        </Box>
      )}
    </div>
  );
}

const ModalWaterFactorControl: React.FC<ModalWaterFactorControlProps> = ({ open, onClose, vehicle }) => {

  const { t } = useTranslation();

  // region States
  const [tabValue, setTabValue] = useState(0);
  // endregion States
  // region Functions

  /**
   * Handles the change event when a tab selection is changed.
   *
   * @param {React.ChangeEvent<{}>} event - The event object triggered by the tab change.
   * @param {number} newValue - The numeric value of the newly selected tab.
   */
  const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
    setTabValue(newValue);
  };

  // endregion Functions

  return (
    <Container id="modalWaterFactorControl">
      <Dialog
        open={open}
        onClose={onClose}
        scroll="paper"
        container={document.getElementById("modalWaterFactorControl")}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle className="mHeader">
          <div className="content">
            <div className="title">{t(VehiclesModuleMessages.waterFactorControlTitle)}</div>
            <div className="subtitle">{`${vehicle?.code} - ${vehicle?.description}`}</div>
          </div>
        </DialogTitle>
        <DialogContent dividers className="mContent">
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="water factor tabs">
            <Tab label={t(VehiclesModuleMessages.waterFactorControlTab)} />
            <Tab label={t(VehiclesModuleMessages.waterFactorHistoryTab)} />
          </Tabs>
          <TabPanel value={tabValue} index={0}>
            <WaterFactorControlTab vehicle={vehicle} onClose={onClose} />
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <WaterFactorHistoryTab vehicle={vehicle} isVisible={tabValue === 1} />
          </TabPanel>
        </DialogContent>
        <DialogActions className="mFooter">
          <Button disableRipple onClick={onClose} color="primary">{t(GlobalMessages.close)}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ModalWaterFactorControl;
